import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

type Palette = "kairos" | "bkry";
type Mode = "light" | "dark";
type Font = "rubik" | "roboto";

interface Theme {
  palette: Palette;
  mode: Mode;
  font: Font;
}

interface ThemeContextValue extends Theme {
  setPalette: (palette: Palette) => void;
  setMode: (mode: Mode) => void;
  setFont: (font: Font) => void;
}

const STORAGE_KEY = "kairos-theme";
const DEFAULT_THEME: Theme = {
  palette: "kairos",
  mode: "light",
  font: "rubik",
};

function loadTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<Theme>;
      return {
        palette: parsed.palette === "bkry" ? "bkry" : "kairos",
        mode: parsed.mode === "dark" ? "dark" : "light",
        font: parsed.font === "roboto" ? "roboto" : "rubik",
      };
    }
  } catch {
    // ignore
  }
  return DEFAULT_THEME;
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.setAttribute("data-palette", theme.palette);
  root.setAttribute("data-font", theme.font);
  if (theme.mode === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(loadTheme);

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
  }, [theme]);

  const setPalette = (palette: Palette) => setTheme((t) => ({ ...t, palette }));
  const setMode = (mode: Mode) => setTheme((t) => ({ ...t, mode }));
  const setFont = (font: Font) => setTheme((t) => ({ ...t, font }));

  return (
    <ThemeContext.Provider value={{ ...theme, setPalette, setMode, setFont }}>
      {children}
    </ThemeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
